/* eslint-env browser */
// SOS reports (wave 3, W2_Portal_Tables look): the reports in the modern
// table with the pending ones first (newest first inside each), Pending /
// Processed chips with their counts above it; a row's button opens the
// report, where staff tag the issue type(s), add notes and mark it processed.
//
// Data, exactly as before: the SOS list already in the store (sosdata.sos,
// the same source as the bell count in the menu); nothing new is read.
// "Mark as processed" calls the same editSos({ ...report, processed: true,
// processedBy, processDate, comments, issueTypes }, 'Update') with the plain
// record (material-table used to add its own `tableData` key, which was then
// saved too). The phone is hidden in demo mode; exports for admins (CSV only
// with critical edits on), as before.
// Call-centre feedback (29 Oct 2026): a copy button beside each number copies
// it without spaces (+251964540448) for MicroSIP; it never opens the row and
// is not there while numbers are hidden.
import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SosOutlinedIcon from '@mui/icons-material/SosOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { tokens as c, toneChipSx } from '../theme';
import { showToast } from '../components/feedback';
import { formatAddisDateTime } from '../common/exportUtils';
import ClickableUserLink from '../components/ClickableUserLink';
import { CopyButton, DataTable, ChipGroup, ActiveFilters, StatusChip, InfoRow, FieldLabel, SrOnly, ALL, calmCaps, phoneMatches, toMs } from '../components/modern';

// Issue categories a call-center agent can tag an SOS with while processing it.
// Stored on the SOS record as `issueTypes: [<key>, …]`. `label` is the English
// defaultValue shown while the key is missing from the loaded language pack.
const SOS_ISSUE_TYPES = [
  { key: 'gps', labelKey: 'sos_issue_gps', label: 'GPS' },
  { key: 'price_override', labelKey: 'sos_issue_price_override', label: 'Booking Price Override' },
  { key: 'topup', labelKey: 'sos_issue_topup', label: 'Top-up' },
  { key: 'compliance', labelKey: 'sos_issue_compliance', label: 'Compliance' },
  { key: 'mistake_tab', labelKey: 'sos_issue_mistake_tab', label: 'Mistake Tab' },
  { key: 'no_response', labelKey: 'no_response_tab', label: 'No Response' },
  { key: 'other', labelKey: 'sos_issue_other', label: 'Other Booking related issue' },
];

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

// Pending reports first, newest first inside each group.
const PENDING_WEIGHT = 1e15;
const urgencyOf = (row) => (row.processed ? 0 : PENDING_WEIGHT) + (toMs(row.complainDate) || 0);

const Sos = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const settings = useSelector((state) => state.settingsdata.settings);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { editSos } = api;
  const sosdata = useSelector((state) => state.sosdata);
  const data = useMemo(() => (Array.isArray(sosdata.sos) ? sosdata.sos : []), [sosdata.sos]);

  const allowPrivate = !!settings.AllowCriticalEditsAdmin;
  const hidden = t('hidden_demo');
  const phoneWord = t('phone', { defaultValue: 'Phone number' });
  const [status, setStatus] = useState(ALL); // 'all' | 'pending' | 'processed'

  const counts = useMemo(() => {
    let processed = 0;
    data.forEach((row) => {
      if (row.processed) processed += 1;
    });
    return { all: data.length, processed, pending: data.length - processed };
  }, [data]);

  const rows = useMemo(() => {
    if (status === 'pending') return data.filter((row) => !row.processed);
    if (status === 'processed') return data.filter((row) => !!row.processed);
    return data;
  }, [data, status]);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSos, setSelectedSos] = useState(null);
  const [comments, setComments] = useState('');
  const [processing, setProcessing] = useState(false);
  const [issueTypes, setIssueTypes] = useState([]);

  const toggleIssueType = (key) => {
    setIssueTypes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleProcessSos = (rowData) => {
    setSelectedSos(rowData);
    setComments(rowData.comments || '');
    // Pre-tick categories already saved on the record (view/re-process).
    setIssueTypes(Array.isArray(rowData.issueTypes) ? rowData.issueTypes : []);
    setOpenDialog(true);
  };

  const handleConfirmProcess = async () => {
    if (!comments.trim()) {
      showToast.error(t('comments_required'));
      return;
    }

    setProcessing(true);

    // Update SOS with processing information
    const updatedSos = {
      ...selectedSos,
      processed: true,
      processedBy: auth.profile && auth.profile.email ? auth.profile.email : auth.profile.uid,
      processDate: new Date().getTime(),
      comments: comments.trim(),
      issueTypes,
    };

    // Process the SOS
    dispatch(editSos(updatedSos, 'Update'));

    setProcessing(false);
    setOpenDialog(false);
    setComments('');
    setSelectedSos(null);
    setIssueTypes([]);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setComments('');
    setSelectedSos(null);
    setIssueTypes([]);
  };

  const statusLabel = (row) => (row.processed ? t('processed') : t('pending'));
  const linkType = (row) => (row.user_type === 'driver' ? 'driver' : row.user_type === 'customer' ? 'customer' : 'auto');
  const issueLabels = (row) =>
    Array.isArray(row.issueTypes)
      ? row.issueTypes.map((k) => {
          const it = SOS_ISSUE_TYPES.find((x) => x.key === k);
          return it ? t(it.labelKey, { defaultValue: it.label }) : k;
        })
      : [];

  const columns = [
    { id: 'bookingId', title: t('id'), field: 'bookingId', nowrap: true },
    {
      id: 'user_name',
      title: t('name'),
      field: 'user_name',
      render: (rowData) =>
        rowData.user_name ? (
          <ClickableUserLink
            value={rowData.user_name}
            type={linkType(rowData)}
            searchField="name"
            userId={rowData.uid}
            iconType={rowData.user_type === 'driver' ? 'car' : 'person'}
          />
        ) : (
          '-'
        ),
    },
    {
      id: 'contact',
      title: t('contact'),
      field: 'contact',
      nowrap: true,
      sortValue: (row) => (allowPrivate ? row.contact || '' : ''),
      render: (rowData) =>
        allowPrivate ? (
          rowData.contact ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
              <ClickableUserLink value={rowData.contact} type={linkType(rowData)} searchField="phone" userId={rowData.uid} iconType="phone" />
              <CopyButton value={rowData.contact} phone what={phoneWord} sx={{ my: -0.5 }} />
            </Box>
          ) : (
            '-'
          )
        ) : (
          hidden
        ),
    },
    { id: 'user_type', title: t('user_type'), field: 'user_type', render: (row) => (row.user_type ? t(row.user_type, { defaultValue: row.user_type }) : '-') },
    { id: 'complainDate', title: t('complain_date'), field: 'complainDate', type: 'datetime' },
    {
      id: 'processed',
      title: t('status'),
      field: 'processed',
      sortValue: urgencyOf,
      defaultSort: 'desc',
      render: (rowData) => (
        <StatusChip
          tone={rowData.processed ? 'success' : 'warning'}
          icon={rowData.processed ? <CheckRoundedIcon /> : <ScheduleRoundedIcon />}
          label={statusLabel(rowData)}
        />
      ),
    },
    {
      // Open the report: "Process SOS" while pending, "View details" once processed.
      id: 'open',
      title: t('action'),
      hideable: false,
      sortable: false,
      searchable: false,
      export: false,
      render: (rowData) =>
        rowData.processed ? (
          <Button variant="text" size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => handleProcessSos(rowData)} sx={{ whiteSpace: 'nowrap' }}>
            {t('view_details')}
          </Button>
        ) : (
          <Button variant="outlined" color="neutral" size="small" startIcon={<CheckCircleOutlineRoundedIcon />} onClick={() => handleProcessSos(rowData)} sx={{ whiteSpace: 'nowrap' }}>
            {t('process_sos')}
          </Button>
        ),
    },
    { id: 'processedBy', title: t('processedBy'), field: 'processedBy' },
    { id: 'processDate', title: t('processDate'), field: 'processDate', type: 'datetime' },
    {
      id: 'issueTypes',
      title: t('sos_issue_types', { defaultValue: 'Issue Type(s)' }),
      field: 'issueTypes',
      sortable: false,
      searchValue: (row) => issueLabels(row).join(' '),
      render: (rowData) => {
        const labels = issueLabels(rowData);
        return labels.length ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 260 }}>
            {labels.map((label, i) => (
              <Box key={`${label}-${i}`} component="span" sx={{ ...toneChipSx('neutral'), px: 1, borderRadius: '13px', fontSize: 12.5, lineHeight: '24px', whiteSpace: 'nowrap' }}>
                {label}
              </Box>
            ))}
          </Box>
        ) : (
          '-'
        );
      },
    },
    {
      id: 'comments',
      title: t('notes'),
      field: 'comments',
      render: (rowData) =>
        rowData.comments ? (
          <Tooltip title={rowData.comments}>
            <Box component="span" tabIndex={0} sx={{ maxWidth: 220, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
              {rowData.comments}
            </Box>
          </Tooltip>
        ) : null,
    },
  ];

  // The same CSV columns as before (issue types in English, joined with "; ");
  // dates in Addis Ababa 12-hour time.
  const exportColumns = [
    { title: t('id'), field: 'bookingId' },
    { title: t('name'), field: 'user_name' },
    { title: t('contact'), value: (row) => (allowPrivate ? row.contact || '' : hidden) },
    { title: t('user_type'), field: 'user_type' },
    { title: t('complain_date'), field: 'complainDate', type: 'datetime' },
    { title: t('status'), value: statusLabel },
    { title: t('processedBy'), field: 'processedBy' },
    { title: t('processDate'), field: 'processDate', type: 'datetime' },
    {
      title: t('sos_issue_types', { defaultValue: 'Issue Type(s)' }),
      value: (row) =>
        Array.isArray(row.issueTypes)
          ? row.issueTypes
              .map((k) => {
                const it = SOS_ISSUE_TYPES.find((x) => x.key === k);
                return it ? it.label : k;
              })
              .join('; ')
          : '',
    },
    { title: t('notes'), field: 'comments' },
  ];

  // Booking id, name, type, notes and issue types always; the phone only when shown.
  const searchFn = (row, q, ql) => {
    const text = [row.bookingId, row.user_name, row.user_type, row.comments, row.processedBy, statusLabel(row), ...issueLabels(row)]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (text.includes(ql)) return true;
    return allowPrivate && phoneMatches(row.contact, q);
  };

  const statusOptions = [
    { value: ALL, label: t('all', { defaultValue: 'All' }), count: counts.all },
    { value: 'pending', label: t('pending'), count: counts.pending },
    { value: 'processed', label: t('processed'), count: counts.processed },
  ];
  const statusGroup = t('status');
  const tokens = status !== ALL ? [{ key: 'status', group: statusGroup, value: (statusOptions.find((o) => o.value === status) || {}).label }] : [];
  const title = calmCaps(t('sos_title', { defaultValue: 'SOS' }));
  const isAdmin = auth.profile.usertype === 'admin';
  const readOnly = !!(selectedSos && selectedSos.processed);
  const loading = !!sosdata.loading && !sosdata.sos;

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5, columnGap: 3 }}>
        <ChipGroup label={statusGroup} value={status} options={statusOptions} onChange={setStatus} />
      </Paper>

      <DataTable
        title={title}
        rows={rows}
        columns={columns}
        loading={loading}
        countText={({ shown, total, query }) =>
          query || status !== ALL
            ? t('table_count_shown', { shown: fmt(query ? shown : total), total: fmt(counts.all), defaultValue: '{{shown}} of {{total}}' })
            : t('sos_count', {
                count: total,
                n: fmt(total),
                defaultValue_one: '{{n}} SOS report',
                defaultValue_other: '{{n}} SOS reports',
                defaultValue: '{{n}} SOS reports',
              })
        }
        subtitle={counts.pending ? t('sos_pending_count', { count: counts.pending, n: fmt(counts.pending), defaultValue: '{{n}} pending' }) : undefined}
        headerContent={tokens.length ? <ActiveFilters tokens={tokens} onRemove={() => setStatus(ALL)} onClearAll={() => setStatus(ALL)} /> : null}
        searchFn={searchFn}
        resortKey={allowPrivate ? 'private' : 'demo'}
        onRowClick={handleProcessSos}
        rowLabel={(row) => row.user_name || row.bookingId || ''}
        rowSx={(row) => (row.processed ? null : { '& > td': { fontWeight: 600 } })}
        prefsKey="sos"
        exportCsv={isAdmin && allowPrivate}
        exportPrint={isAdmin}
        exportColumns={exportColumns}
        exportFileName="sos"
        exportTitle={title}
        minWidth={1320}
        emptyState={({ query }) =>
          query
            ? null
            : status !== ALL
              ? {
                  icon: <FilterAltOffOutlinedIcon />,
                  title: t('sos_empty_filtered', { defaultValue: 'No SOS reports with this status' }),
                  actionLabel: t('clear_filters', { defaultValue: 'Clear filters' }),
                  onAction: () => setStatus(ALL),
                }
              : { icon: <SosOutlinedIcon />, title: t('sos_empty', { defaultValue: 'No SOS reports' }) }
        }
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={fullScreen} aria-labelledby="sos-dialog-title">
        <DialogTitle id="sos-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1.5 }}>
          <Box component="span" sx={{ flex: 1, fontSize: 18, fontWeight: 700, color: c.INK }}>
            {readOnly ? t('sos_details') : t('process_sos_title')}
          </Box>
          <IconButton onClick={handleCloseDialog} aria-label={t('close_dialog', { defaultValue: 'Close' })} disabled={processing}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSos ? (
            <Box component="dl" sx={{ m: 0, mb: 2 }}>
              <InfoRow label={t('booking_id')}>{selectedSos.bookingId}</InfoRow>
              <InfoRow label={t('name')}>{selectedSos.user_name}</InfoRow>
              <InfoRow label={t('contact')}>
                {allowPrivate ? (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                    {selectedSos.contact}
                    <CopyButton value={selectedSos.contact} phone what={phoneWord} sx={{ my: -0.5 }} />
                  </Box>
                ) : (
                  hidden
                )}
              </InfoRow>
              <InfoRow label={t('user_type')}>{selectedSos.user_type ? t(selectedSos.user_type, { defaultValue: selectedSos.user_type }) : null}</InfoRow>
              <InfoRow label={t('complain_date')}>{formatAddisDateTime(selectedSos.complainDate)}</InfoRow>
              {selectedSos.processed ? (
                <>
                  <InfoRow label={t('processedBy')}>{selectedSos.processedBy}</InfoRow>
                  <InfoRow label={t('processDate')}>{formatAddisDateTime(selectedSos.processDate)}</InfoRow>
                </>
              ) : null}
            </Box>
          ) : null}
          <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, mb: 2 }}>
            <Typography component="legend" sx={{ fontSize: 14.5, fontWeight: 600, color: c.INK, mb: 0.5, p: 0 }}>
              {t('sos_issue_types', { defaultValue: 'Issue Type(s)' })}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, columnGap: 2 }}>
              {SOS_ISSUE_TYPES.map((it) => (
                <FormControlLabel
                  key={it.key}
                  control={
                    <Checkbox checked={issueTypes.includes(it.key)} onChange={() => toggleIssueType(it.key)} disabled={processing || readOnly} />
                  }
                  label={<Box component="span" sx={{ fontSize: 14 }}>{t(it.labelKey, { defaultValue: it.label })}</Box>}
                  sx={{ m: 0, minHeight: 44 }}
                />
              ))}
            </Box>
          </Box>
          <FieldLabel htmlFor="sos-notes">{t('notes_comments')}</FieldLabel>
          <TextField
            id="sos-notes"
            autoFocus={!readOnly}
            fullWidth
            multiline
            minRows={4}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            disabled={processing || readOnly}
            inputProps={{ 'aria-describedby': 'sos-notes-hint' }}
          />
          <SrOnly id="sos-notes-hint">{readOnly ? t('notes_readonly') : t('notes_help')}</SrOnly>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button variant="text" color="neutral" onClick={handleCloseDialog} disabled={processing}>
            {readOnly ? calmCaps(t('close')) : t('cancel')}
          </Button>
          {!readOnly ? (
            <Button onClick={handleConfirmProcess} variant="contained" startIcon={<CheckRoundedIcon />} disabled={processing || !comments.trim()}>
              {processing ? t('form_saving', { defaultValue: 'Saving…' }) : t('mark_as_processed')}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sos;
